import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { api } from '../../services/api';
import ImageUploader from '../../components/ImageUploader';
import ProgressBar from '../../components/ProgressBar';
import { Input, TextArea } from '../../components/ui/Input';
import { Button } from '../../components/ui/Button';
import { Card, CardContent } from '../../components/ui/Card';

const Container = styled.div`
  max-width: 800px;
  margin: 0 auto;
`;

const SectionHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin: 2rem 0 1rem;
  border-bottom: 1px solid #eee;
  padding-bottom: 0.5rem;
  h3 {
    color: ${({ theme }) => theme.colors.secondary};
    margin: 0;
  }
`;

const FormGroup = styled.div`
  margin-bottom: 1.5rem;
  label {
    display: block;
    margin-bottom: 0.5rem;
    font-weight: 500;
  }
`;

const AboutAdmin = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');
  const [activeUpload, setActiveUpload] = useState(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const res = await api.get('/about');
      setData(res);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (section, field, value, index = null) => {
    setData(prev => {
      const newData = { ...prev };
      if (index !== null) {
        if (!newData.otherSections) newData.otherSections = [];
        if (!newData.otherSections[index]) newData.otherSections[index] = {};
        newData.otherSections[index][field] = value;
      } else {
        if (!newData[section]) newData[section] = {};
        newData[section][field] = value;
      }
      return newData;
    });
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put('/about', data);
      setMsg('Saved successfully!');
      setTimeout(() => setMsg(''), 3000);
    } catch (err) {
      setMsg('Error saving');
    } finally {
      setSaving(false);
    }
  };

  const handleImageUpload = async (section, file, index = null) => {
    if (!file) return;
    const uploadKey = index !== null ? `${section}-${index}` : section;
    setActiveUpload({ key: uploadKey, progress: 0 });

    const timer = setInterval(() => {
        setActiveUpload(prev => {
            if(prev && prev.key === uploadKey && prev.progress < 90) {
                return { ...prev, progress: prev.progress + 10 };
            }
            return prev;
        });
    }, 100);

    const formData = new FormData();
    formData.append('image', file);
    
    let endpoint;
    if (index !== null) {
        endpoint = `/about/other-image/${index}`;
    } else {
        endpoint = `/about/image/${section}`;
    }
    
    try {
      const res = await api.upload(endpoint, formData);
      
      clearInterval(timer);
      setActiveUpload({ key: uploadKey, progress: 100 });

      // The backend returns the updated section object
      setData(prev => {
          const newData = { ...prev };
          if (index !== null) {
              newData.otherSections[index] = res;
          } else {
              newData[section] = res;
          }
          return newData;
      });

      setTimeout(() => setActiveUpload(null), 500);

    } catch(err) {
      clearInterval(timer);
      console.error('Upload failed', err);
      alert('Upload failed. Check console.');
      setActiveUpload(null);
    }
  };

  if (loading) return <div>Loading...</div>;
  if (!data) return <div>Error</div>;

  return (
    <Container>
      <h2>Manage About Page</h2>
      {msg && <div style={{color:'green', marginBottom:'1rem'}}>{msg}</div>}
      <Button onClick={handleSave} style={{marginBottom:'2rem'}} disabled={saving}>
        {saving ? 'Saving...' : 'Save Changes'}
      </Button>

      {['bio', 'mission', 'vision', 'goals'].map(key => (
        <Card key={key} style={{marginBottom:'1rem'}}>
          <CardContent>
            <SectionHeader><h3>{key.charAt(0).toUpperCase() + key.slice(1)}</h3></SectionHeader>
            <FormGroup>
              <label>Title</label>
              <Input 
                value={data[key]?.title || ''} 
                onChange={(e) => handleChange(key, 'title', e.target.value)} 
              />
            </FormGroup>
            <FormGroup>
              <label>Content</label>
              <TextArea 
                value={data[key]?.content || ''} 
                onChange={(e) => handleChange(key, 'content', e.target.value)} 
              />
            </FormGroup>
            <FormGroup>
              <label>Image</label>
              <ImageUploader 
                previewUrl={data[key]?.image} 
                onImageSelect={(file) => handleImageUpload(key, file)} 
              />
              {activeUpload && activeUpload.key === key && (
                  <ProgressBar progress={activeUpload.progress} />
              )}
            </FormGroup>
          </CardContent>
        </Card>
      ))}
      
      {/* Other Sections Logic - simplified for now */}
    </Container>
  );
};

export default AboutAdmin;
